import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMobileAlt, faLaptop, faCamera, faSpinner, faXmark
} from '@fortawesome/free-solid-svg-icons';
import { useSwap } from '../context/SwapContext';
import { useNotifications } from '../context/NotificationContext';
import { uploadImage as uploadImageWithFallback } from '../services/imageUploadService';
import '../assets/css/swap-offer-form.css';

/**
 * SwapOfferForm Component
 *
 * Allows buyers to submit a device for swap offer
 * - Device information collection
 * - Image upload with validation
 * - Condition and battery health rating
 * - Repair history tracking
 * - Real-time validation
 */
const SwapOfferForm = ({
  productId,                    // Product to swap for (required)
  onSuccess = () => {},         // Callback after successful submission
  onCancel = () => {},          // Callback when user cancels
  initialData = null,           // Pre-fill form with data
  showSummary = true,           // Show summary before submit
}) => {
  const { submitSwapOffer, loading: contextLoading } = useSwap();
  const { showCustomToast } = useNotifications();
  const fileInputRef = useRef(null);

  // ── Form State ──────────────────────────────────────────────
  const [formData, setFormData] = useState({
    device_type: initialData?.device_type || '',
    model: initialData?.model || '',
    storage: initialData?.storage || '',
    condition_rating: initialData?.condition_rating || 5,
    battery_health: initialData?.battery_health || 100,
    face_id_status: initialData?.face_id_status || '',
    repair_history: initialData?.repair_history || '',
    ram: initialData?.ram || '', // For Laptop
    cpu: initialData?.cpu || '', // For Laptop
    graphics: initialData?.graphics || '', // For Laptop (optional)
    terms: initialData?.terms || '', // Swap terms/notes
    // IMEI for the device being offered — required (IMEI 2 optional, for
    // dual-SIM). Submitted once and never shown back: the listing seller
    // never sees it, and neither does the initiator after submitting.
    // Admin-only, for warehouse inspection.
    imei1: initialData?.imei1 || '',
    imei2: initialData?.imei2 || '',
  });

  const [images, setImages] = useState(initialData?.images || []);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1); // 1: Info, 2: Images, 3: Review
  const [showConfirmation, setShowConfirmation] = useState(false);

  // ── Device Type Options ─────────────────────────────────────
  const deviceTypes = [
    { value: 'iOS', label: 'iPhone', icon: faMobileAlt },
    { value: 'Android', label: 'Android', icon: faMobileAlt },
    { value: 'Laptop', label: 'Laptop', icon: faLaptop },
  ];

  const faceIdStatuses = [
    { value: 'yes', label: 'Working' },
    { value: 'no', label: 'Not Working' },
    { value: 'partially', label: 'Partially' },
  ];

  // ── Validation ──────────────────────────────────────────────
  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1) {
      if (!formData.device_type) newErrors.device_type = 'Device type is required';
      if (!formData.model?.trim()) newErrors.model = 'Model is required';
      if (!formData.storage?.trim()) newErrors.storage = 'Storage is required';
      if (formData.condition_rating < 1 || formData.condition_rating > 10) {
        newErrors.condition_rating = 'Condition must be between 1-10';
      }
      if (!/^\d{15}$/.test((formData.imei1 || '').trim())) {
        newErrors.imei1 = 'IMEI 1 is required and must be exactly 15 digits';
      }
      if (formData.imei2 && !/^\d{15}$/.test(formData.imei2.trim())) {
        newErrors.imei2 = 'IMEI 2 must be exactly 15 digits if provided';
      }

      // Device-specific validations
      if (formData.device_type === 'iOS') {
        if (formData.battery_health < 0 || formData.battery_health > 100) {
          newErrors.battery_health = 'Battery health must be between 0-100';
        }
        if (!formData.face_id_status) {
          newErrors.face_id_status = 'Face ID status is required for iPhone';
        }
      }

      if (formData.device_type === 'Laptop') {
        if (!formData.ram?.trim()) newErrors.ram = 'RAM is required';
        if (!formData.cpu?.trim()) newErrors.cpu = 'CPU is required';
      }
    }

    if (stepNum === 2) {
      if (images.length < 5) newErrors.images = `At least 5 images required (${images.length}/5)`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Handle Image Upload — uploads each file to server, stores returned URL ──
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          showCustomToast({
            type: 'warning',
            title: 'Invalid File',
            message: `${file.name} is not an image`,
          });
          continue;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          showCustomToast({
            type: 'warning',
            title: 'File Too Large',
            message: `${file.name} exceeds 5MB limit`,
          });
          continue;
        }

        try {
          const { url } = await uploadImageWithFallback(file);

          if (url) {
            setImages(prev =>
              prev.length >= 10 ? prev : [...prev, url]
            );
          } else {
            showCustomToast({
              type: 'error',
              title: 'Upload Failed',
              message: `No URL returned for ${file.name}`,
            });
          }
        } catch (uploadErr) {
          showCustomToast({
            type: 'error',
            title: 'Upload Failed',
            message: uploadErr.message || `Failed to upload ${file.name}`,
          });
        }
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Handle Image Click to Upload ────────────────────────────
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  // ── Remove Image ────────────────────────────────────────────
  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // ── Handle Form Submit ──────────────────────────────────────
  const handleSubmit = async () => {
    // Final validation
    if (!validateStep(1) || !validateStep(2)) {
      showCustomToast({
        type: 'error',
        title: 'Validation Failed',
        message: 'Please complete all required fields',
      });
      return;
    }

    if (!productId) {
      showCustomToast({
        type: 'error',
        title: 'Missing Product',
        message: 'No product selected to swap for',
      });
      return;
    }

    setLoading(true);
    try {
      const offerData = {
        listing_product_id: productId,
        device_type: formData.device_type,
        model: formData.model.trim(),
        storage: formData.storage.trim(),
        condition_rating: parseInt(formData.condition_rating),
        battery_health: formData.device_type === 'iOS' ? parseInt(formData.battery_health) : undefined,
        face_id_status: formData.device_type === 'iOS' ? formData.face_id_status : undefined,
        repair_history: formData.repair_history.trim() || undefined,
        ram: formData.device_type === 'Laptop' ? formData.ram.trim() : undefined,
        cpu: formData.device_type === 'Laptop' ? formData.cpu.trim() : undefined,
        graphics: formData.device_type === 'Laptop' && formData.graphics?.trim() ? formData.graphics.trim() : undefined,
        terms: formData.terms.trim() || undefined,
        images,
        imei1: formData.imei1.trim(),
        imei2: formData.imei2?.trim() || undefined,
      };

      const result = await submitSwapOffer(offerData);

      // Success
      showCustomToast({
        type: 'success',
        title: 'Swap Offer Submitted!',
        message: `Your offer ${result.swap_number} is now waiting for seller review`,
        duration: 5000,
      });

      // Reset form
      setFormData({
        device_type: '',
        model: '',
        storage: '',
        condition_rating: 5,
        battery_health: 100,
        face_id_status: '',
        repair_history: '',
        ram: '',
        cpu: '',
        graphics: '',
        terms: '',
        imei1: '',
        imei2: '',
      });
      setImages([]);
      setStep(1);
      setShowConfirmation(false);

      // Call success callback
      onSuccess(result);
    } catch (err) {
      console.error('Submission error:', err);
      // Error already shown via toast in SwapContext
    } finally {
      setLoading(false);
    }
  };

  // ── Render Step 1: Device Information ───────────────────────
  const renderStep1 = () => (
    <motion.div
      key="step1"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="swap-form-section"
    >
      <h3>Device Information</h3>

      {/* Device Type Selection */}
      <div className="form-group">
        <label>Device Type *</label>
        <div className="device-type-grid">
          {deviceTypes.map((device) => (
            <motion.button
              key={device.value}
              type="button"
              className={`device-option ${
                formData.device_type === device.value ? 'active' : ''
              }`}
              onClick={() => {
                setFormData({ ...formData, device_type: device.value });
                setErrors({ ...errors, device_type: '' });
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FontAwesomeIcon icon={device.icon} className="icon" />
              <span className="label">{device.label}</span>
            </motion.button>
          ))}
        </div>
        {errors.device_type && (
          <p className="error-message">{errors.device_type}</p>
        )}
      </div>

      {/* Model Input */}
      <div className="form-group">
        <label>Device Model *</label>
        <input
          type="text"
          placeholder="e.g., iPhone 13 Pro, Samsung Galaxy S24"
          value={formData.model}
          onChange={(e) => {
            setFormData({ ...formData, model: e.target.value });
            setErrors({ ...errors, model: '' });
          }}
          className={errors.model ? 'error' : ''}
        />
        {errors.model && <p className="error-message">{errors.model}</p>}
      </div>

      {/* Storage Input */}
      <div className="form-group">
        <label>Storage Capacity *</label>
        <input
          type="text"
          placeholder="e.g., 256GB, 512GB"
          value={formData.storage}
          onChange={(e) => {
            setFormData({ ...formData, storage: e.target.value });
            setErrors({ ...errors, storage: '' });
          }}
          className={errors.storage ? 'error' : ''}
        />
        {errors.storage && <p className="error-message">{errors.storage}</p>}
      </div>

      {/* IMEI — recorded once for warehouse inspection. Never shown to the
          seller, and not shown back to you after submitting. */}
      <div className="form-group">
        <label>IMEI 1 *</label>
        <input
          type="text"
          inputMode="numeric"
          maxLength={15}
          placeholder="15-digit IMEI (dial *#06# to find it)"
          value={formData.imei1}
          onChange={(e) => {
            setFormData({ ...formData, imei1: e.target.value.replace(/\D/g, '') });
            setErrors({ ...errors, imei1: '' });
          }}
          className={errors.imei1 ? 'error' : ''}
        />
        {errors.imei1 && <p className="error-message">{errors.imei1}</p>}
        <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
          Used only by AutoboyExpress for device inspection — never shared with the seller.
        </small>
      </div>

      <div className="form-group">
        <label>IMEI 2 (optional — dual-SIM)</label>
        <input
          type="text"
          inputMode="numeric"
          maxLength={15}
          placeholder="Second 15-digit IMEI, if your device has one"
          value={formData.imei2}
          onChange={(e) => {
            setFormData({ ...formData, imei2: e.target.value.replace(/\D/g, '') });
            setErrors({ ...errors, imei2: '' });
          }}
          className={errors.imei2 ? 'error' : ''}
        />
        {errors.imei2 && <p className="error-message">{errors.imei2}</p>}
      </div>

      {/* Condition Rating Slider */}
      <div className="form-group">
        <label>Device Condition Rating *</label>
        <div className="slider-container">
          <input
            type="range"
            min="1"
            max="10"
            value={formData.condition_rating}
            onChange={(e) => {
              setFormData({
                ...formData,
                condition_rating: parseInt(e.target.value),
              });
              setErrors({ ...errors, condition_rating: '' });
            }}
            className="slider"
          />
          <div className="slider-labels">
            <span>Poor (1)</span>
            <span className="current-value">{formData.condition_rating}/10</span>
            <span>Excellent (10)</span>
          </div>
        </div>
        {errors.condition_rating && (
          <p className="error-message">{errors.condition_rating}</p>
        )}
      </div>

      {/* Battery Health Slider (iOS & Android only) */}
      {formData.device_type !== 'Laptop' && (
        <div className="form-group">
          <label>Battery Health (%) {formData.device_type === 'iOS' ? '*' : ''}</label>
          <div className="slider-container">
            <input
              type="range"
              min="0"
              max="100"
              value={formData.battery_health}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  battery_health: parseInt(e.target.value),
                });
                setErrors({ ...errors, battery_health: '' });
              }}
              className="slider"
            />
            <div className="slider-labels">
              <span>0%</span>
              <span className="current-value">{formData.battery_health}%</span>
              <span>100%</span>
            </div>
          </div>
          {errors.battery_health && (
            <p className="error-message">{errors.battery_health}</p>
          )}
        </div>
      )}

      {/* Face ID Status (ios only) */}
      {formData.device_type === 'iOS' && (
        <div className="form-group">
          <label>Face ID Status *</label>
          <select
            value={formData.face_id_status}
            onChange={(e) => {
              setFormData({ ...formData, face_id_status: e.target.value });
              setErrors({ ...errors, face_id_status: '' });
            }}
            className={errors.face_id_status ? 'error' : ''}
          >
            <option value="">Select status</option>
            {faceIdStatuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          {errors.face_id_status && (
            <p className="error-message">{errors.face_id_status}</p>
          )}
        </div>
      )}

      {/* Laptop-specific fields */}
      {formData.device_type === 'Laptop' && (
        <>
          {/* RAM */}
          <div className="form-group">
            <label>RAM *</label>
            <input
              type="text"
              placeholder="e.g., 16GB, 32GB"
              value={formData.ram}
              onChange={(e) => {
                setFormData({ ...formData, ram: e.target.value });
                setErrors({ ...errors, ram: '' });
              }}
              className={errors.ram ? 'error' : ''}
            />
            {errors.ram && <p className="error-message">{errors.ram}</p>}
          </div>

          {/* CPU */}
          <div className="form-group">
            <label>CPU *</label>
            <input
              type="text"
              placeholder="e.g., Intel Core i7, Apple M2 Pro"
              value={formData.cpu}
              onChange={(e) => {
                setFormData({ ...formData, cpu: e.target.value });
                setErrors({ ...errors, cpu: '' });
              }}
              className={errors.cpu ? 'error' : ''}
            />
            {errors.cpu && <p className="error-message">{errors.cpu}</p>}
          </div>

          {/* Graphics Card (optional) */}
          <div className="form-group">
            <label>Graphics Card (Optional)</label>
            <input
              type="text"
              placeholder="e.g., NVIDIA RTX 4060, Integrated Intel Iris"
              value={formData.graphics}
              onChange={(e) => {
                setFormData({ ...formData, graphics: e.target.value });
              }}
            />
          </div>
        </>
      )}

      {/* Repair History */}
      <div className="form-group">
        <label>Repair History</label>
        <textarea
          placeholder="Any repairs, replacements, or known issues? (e.g., Screen replaced, Battery replaced)"
          value={formData.repair_history}
          onChange={(e) =>
            setFormData({ ...formData, repair_history: e.target.value })
          }
          rows="3"
        />
      </div>

      {/* Swap Terms/Notes */}
      <div className="form-group">
        <label>Swap Terms/Notes (Optional)</label>
        <textarea
          placeholder="Any additional conditions or notes for this swap? (e.g., Prefer to meet in Lagos, Include charger, etc.)"
          value={formData.terms}
          onChange={(e) =>
            setFormData({ ...formData, terms: e.target.value })
          }
          rows="3"
        />
      </div>
    </motion.div>
  );

  // ── Render Step 2: Image Upload ─────────────────────────────
  const renderStep2 = () => (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="swap-form-section"
    >
      <h3>Device Images</h3>
      <p className="section-description">
        Upload at least 5 clear photos of your device following the guide below.
      </p>

      {/* Required shots guide */}
      <div className="required-shots-guide">
        <p className="guide-title">Required Photo Shots:</p>
        <ol className="shots-list">
          <li>Front screen (display on)</li>
          <li>Back panel</li>
          <li>Left side</li>
          <li>Right side</li>
          <li>Top edge</li>
          <li>Bottom edge (charging port)</li>
          <li>Any damage / special condition close-up</li>
        </ol>
      </div>

      {/* Image Upload Area */}
      <div className="image-upload-area">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleImageUpload(e.target.files)}
          className="hidden-input"
        />

        <motion.button
          type="button"
          className="upload-button"
          onClick={handleImageClick}
          disabled={uploading}
          whileHover={{ scale: uploading ? 1 : 1.05 }}
          whileTap={{ scale: uploading ? 1 : 0.95 }}
        >
          <FontAwesomeIcon
            icon={uploading ? faSpinner : faCamera}
            className="upload-icon"
            spin={uploading}
          />
          <p>{uploading ? 'Uploading...' : 'Click to upload images'}</p>
          {!uploading && <p className="upload-hint">or drag and drop</p>}
        </motion.button>
      </div>

      {/* Image Count */}
      <div className={`image-count ${images.length >= 5 ? 'complete' : 'incomplete'}`}>
        <span>{images.length}/5+ images</span>
        {images.length >= 5 && <span className="checkmark">✓</span>}
      </div>

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="image-preview-grid">
          <AnimatePresence>
            {images.map((image, index) => (
              <motion.div
                key={index}
                className="image-preview-item"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <img src={image} alt={`Preview ${index + 1}`} />
                <div className="image-number">{index + 1}</div>
                <motion.button
                  type="button"
                  className="remove-button"
                  onClick={() => removeImage(index)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </motion.button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {errors.images && (
        <p className="error-message">{errors.images}</p>
      )}
    </motion.div>
  );

  // ── Render Step 3: Review & Confirm ─────────────────────────
  const renderStep3 = () => (
    <motion.div
      key="step3"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="swap-form-section"
    >
      <h3>Review Your Offer</h3>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <label>Device Type</label>
          <p>{formData.device_type}</p>
        </div>
        <div className="summary-card">
          <label>Model</label>
          <p>{formData.model}</p>
        </div>
        <div className="summary-card">
          <label>Storage</label>
          <p>{formData.storage}</p>
        </div>
        <div className="summary-card">
          <label>Condition</label>
          <p>{formData.condition_rating}/10</p>
        </div>
        <div className="summary-card">
          <label>Battery Health</label>
          <p>{formData.battery_health}%</p>
        </div>
        <div className="summary-card">
          <label>Images</label>
          <p>{images.length} uploaded</p>
        </div>
      </div>

      {formData.repair_history && (
        <div className="repair-history-box">
          <label>Repair History</label>
          <p>{formData.repair_history}</p>
        </div>
      )}

      {/* Confirmation Message */}
      <motion.div
        className="confirmation-message"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <p>
          By submitting this offer, you confirm that all information is accurate
          and the device condition matches the provided images.
        </p>
      </motion.div>
    </motion.div>
  );

  // ── Main Render ─────────────────────────────────────────────
  return (
    <motion.div
      className="swap-offer-form-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="form-header">
        <h2>Submit Swap Offer</h2>
        <p>Tell us about your device</p>
      </div>

      {/* Progress Indicator */}
      <div className="progress-indicator">
        {[1, 2, 3].map((num) => (
          <motion.div
            key={num}
            className={`progress-step ${step >= num ? 'active' : ''} ${
              step === num ? 'current' : ''
            }`}
            animate={{
              backgroundColor: step >= num ? '#22C55E' : '#E5E7EB',
            }}
          >
            {num}
          </motion.div>
        ))}
      </div>

      {/* Form Steps */}
      <AnimatePresence mode="wait">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="form-navigation">
        {step > 1 && (
          <motion.button
            type="button"
            className="btn-secondary"
            onClick={() => setStep(step - 1)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Back
          </motion.button>
        )}

        {step < 3 ? (
          <motion.button
            type="button"
            className="btn-primary"
            onClick={() => {
              if (validateStep(step)) {
                setStep(step + 1);
              } else {
                showCustomToast({
                  type: 'error',
                  title: 'Required Fields',
                  message: 'Please fill in all required fields marked with *',
                });
              }
            }}
            disabled={uploading}
            whileHover={{ scale: uploading ? 1 : 1.02 }}
            whileTap={{ scale: uploading ? 1 : 0.98 }}
          >
            {uploading ? 'Uploading...' : 'Next'}
          </motion.button>
        ) : (
          <motion.button
            type="button"
            className="btn-success"
            onClick={() => setShowConfirmation(true)}
            disabled={loading || contextLoading || uploading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading || contextLoading ? 'Submitting...' : uploading ? 'Uploading...' : 'Submit Offer'}
          </motion.button>
        )}

        <motion.button
          type="button"
          className="btn-ghost"
          onClick={onCancel}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Cancel
        </motion.button>
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            className="confirmation-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="confirmation-modal"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <h3>Confirm Swap Offer</h3>
              <p>
                You are about to submit a swap offer for a {formData.model} in{' '}
                {formData.condition_rating}/10 condition. The seller will review your offer and
                respond with a top-up price if interested.
              </p>
              <div className="modal-buttons">
                <button
                  className="btn-secondary"
                  onClick={() => setShowConfirmation(false)}
                  disabled={loading || contextLoading || uploading}
                >
                  Cancel
                </button>
                <button
                  className="btn-success"
                  onClick={handleSubmit}
                  disabled={loading || contextLoading || uploading}
                >
                  {loading || contextLoading ? 'Submitting...' : 'Confirm & Submit'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SwapOfferForm;
